// Layout math for the contributions constellation.
//
// Two layouts share one set of elements: a spiral "arm" of month clusters
// (graph mode) and a 53x7 GitHub-style grid (calendar mode). Every function
// here is pure so the animation loop can call it freely.

export const VIEW_W = 840;
export const VIEW_H_GRAPH = 430;
// The calendar is framed shorter than the constellation; the viewBox height
// tweens between the two so the morph stays smooth.
export const VIEW_H_CALENDAR = 370;
export const CENTER = { x: 430, y: 215 };

// Calendar layout, measured off nathanchapman.dev: 31 day-of-month columns by
// one row per month -- NOT the 53x7 week grid GitHub itself uses.
export const CAL_GUTTER = 94;
export const CAL_RIGHT = 822;
export const CAL_COLS = 31;
export const CAL_COL_PITCH = (CAL_RIGHT - CAL_GUTTER) / CAL_COLS; // 23.4839
export const CAL_CELL_W = CAL_COL_PITCH * 0.56; // 13.151
export const CAL_CELL_H = 8;
export const CAL_ROW_PITCH = 27;
export const CAL_TOP = 14;
export const CAL_LABEL_X = 32;
export const CAL_INSET = (CAL_COL_PITCH - CAL_CELL_W) / 2;

// Palette, also from that site. Level 0 is the one colour that differs between
// the layouts: grey dust in the constellation, near-white cells in the grid.
export const STAR_COLORS = ["#d1d5db", "#a7f3d0", "#6ee7b7", "#34d399", "#267a58"];
export const DUST_COLOR = "#d1d5db";
export const EMPTY_COLOR = "#f9fafb";
export const ROW_RULE_COLOR = "#f3f4f6";

// Spiral arm. Tuned so the widest cluster still sits inside the viewBox.
const ARM_START = -0.62 * Math.PI;
const ARM_SWEEP = 2.9 * Math.PI;
const ARM_R0 = 80;
const ARM_R1 = 195;
const ARM_SQUASH = 0.72;

const STAR_RADII = [1.3, 2.6, 3.5, 4.4, 5.3];

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function easeInOut(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Deterministic [0,1) from a string. The old implementation seeded off
// Y+M+D summed, which collided constantly (2025-03-10 === 2025-10-03) and
// gave the grid a visible periodic structure. Hashing the whole key fixes it.
export function hashUnit(key, salt = 0) {
  const source = `${key}:${salt}`;
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0; // eslint-disable-line no-bitwise
  }
  return (Math.sin(Math.abs(hash)) + 1) / 2;
}

// Point at normalised position t along the spiral arm.
export function armPoint(t) {
  const e = Math.pow(clamp(t, 0, 1), 0.82);
  const angle = ARM_START + e * ARM_SWEEP;
  const radius = ARM_R0 + ARM_R1 * e;
  return {
    x: CENTER.x + Math.cos(angle) * radius,
    y: CENTER.y + Math.sin(angle) * radius * ARM_SQUASH,
  };
}

export function monthCenter(index, count) {
  return armPoint(count <= 1 ? 0 : index / (count - 1));
}

// The arm drawn as a faint stroked path. With only a handful of active days
// this is what keeps the composition from reading as an empty canvas.
export function armPath(steps = 240) {
  let d = "";
  for (let i = 0; i <= steps; i += 1) {
    const p = armPoint(i / steps);
    d += `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }
  return d;
}

// A day scattered around its month's cluster centre, itself a small spiral.
export function graphPos(day, monthCount) {
  const c = monthCenter(day.monthIndex, monthCount);
  const angle = (day.dayOfMonth / 31) * Math.PI * 2.7 + 0.34 * day.monthIndex;
  const radius =
    10 + 0.8 * day.dayOfMonth + 4 * day.level + 9 * hashUnit(day.date, 1);
  return {
    x: c.x + Math.cos(angle) * radius + 10 * (hashUnit(day.date, 2) - 0.5),
    y: c.y + Math.sin(angle) * radius * 0.58 + 8 * (hashUnit(day.date, 3) - 0.5),
  };
}

// Centre of a day's cell: column by day-of-month, row by month.
export function calendarPos(day) {
  return {
    x: CAL_GUTTER + (day.dayOfMonth - 1) * CAL_COL_PITCH + CAL_COL_PITCH / 2,
    y: CAL_TOP + day.monthIndex * CAL_ROW_PITCH + CAL_CELL_H / 2,
  };
}

// An empty day is a wide flat pill; an active day is a circle sized by level.
export function calendarCell(level) {
  if (!level) return { w: CAL_CELL_W, h: CAL_CELL_H, rx: 2 };
  const r = 1.36 + 0.72 * level;
  return { w: r * 2, h: r * 2, rx: r };
}

export function calendarRowY(rowIndex) {
  return CAL_TOP + rowIndex * CAL_ROW_PITCH + CAL_CELL_H / 2;
}

export function mixHex(from, to, t) {
  const parse = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [ar, ag, ab] = parse(from);
  const [br, bg, bb] = parse(to);
  const channel = (a, b) => Math.round(a + (b - a) * t);
  return `rgb(${channel(ar, br)} ${channel(ag, bg)} ${channel(ab, bb)})`;
}

export function starRadius(level) {
  return STAR_RADII[level] || STAR_RADII[0];
}

// ---------------------------------------------------------- starred repos
//
// The third reading: the repositories Carlos has starred, laid out on the same
// spiral arm as the contributions constellation. Month clusters become
// language clusters, so the arm reads as the shape of what he follows.

// Borrowed from GitHub's own star gold exactly the way STAR_COLORS is borrowed
// from its contribution graph. Green is what he shipped; this is what he
// follows, and the two readings never get confused.
//
// The ramp sits high on purpose. Carlos stars famous repositories, so two
// thirds of the field lands in the top two levels -- a ramp centred any darker
// renders the whole sky chocolate brown instead of gold. Level 3 is deliberately
// close to GitHub's own light-mode attention gold (#9a6700), because that is
// where the mass of the data sits and it is the tone the view is remembered by.
export const REPO_COLORS = ["#e0b355", "#d09b2e", "#bd8110", "#a76a06", "#8a5205"];

// log10 of the largest repo the plot expects to hold. 5.7 puts the decade
// boundaries on round numbers: ~30 stars ends level 0, then hundreds,
// thousands, ten-thousands, and 100k+ tops the ramp.
const REPO_LOG_MAX = 5.7;
const REPO_R_MIN = 2;
const REPO_R_SPAN = 3.3;

const CLUSTER_R0 = 5;
const CLUSTER_R1 = 10.5;
// Arc allocated to a language is sqrt(count) plus this floor, so a one-repo
// language still gets enough of the arm to clear its neighbours.
const CLUSTER_ARC_FLOOR = 1.2;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export function repoScale(stars) {
  return clamp(Math.log10(Math.max(stars, 0) + 1) / REPO_LOG_MAX, 0, 1);
}

export function repoLevel(stars) {
  return clamp(Math.floor(repoScale(stars) * 5), 0, 4);
}

export function repoRadius(stars) {
  return REPO_R_MIN + REPO_R_SPAN * repoScale(stars);
}

/**
 * Places each language group on the arm. Position is allocated by cumulative
 * weight rather than by even division, so a 21-repo language occupies more of
 * the arc than a 1-repo one and the clusters stay clear of each other where
 * the spiral is tightest.
 */
export function languageCenters(groups) {
  const weights = groups.map(
    (group) => Math.sqrt(group.repos.length) + CLUSTER_ARC_FLOOR
  );
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  let acc = 0;

  return groups.map((group, index) => {
    const t = (acc + weights[index] / 2) / total;
    acc += weights[index];
    return {
      ...group,
      ...armPoint(t),
      radius: CLUSTER_R0 + CLUSTER_R1 * Math.sqrt(group.repos.length),
    };
  });
}

/**
 * Sunflower packing inside a cluster: the most-starred repo lands nearest the
 * centre and the rest spiral out at the golden angle, squashed vertically to
 * sit in the same ellipse the arm is drawn on.
 */
export function repoPos(cluster, index, count, key) {
  const angle = index * GOLDEN_ANGLE + hashUnit(cluster.key) * Math.PI * 2;
  const radius = cluster.radius * Math.sqrt((index + 0.6) / Math.max(count, 1));
  return {
    x: cluster.x + Math.cos(angle) * radius + 4 * (hashUnit(key, 4) - 0.5),
    y: cluster.y + Math.sin(angle) * radius * 0.75 + 3.5 * (hashUnit(key, 5) - 0.5),
  };
}

// Where a language's name sits. Normally above its cluster; a cluster that
// rides the top of the frame gets its label underneath instead, so a big
// group near the arm's crown never pushes its label out of the viewBox.
export function clusterLabelY(cluster) {
  const above = cluster.y - cluster.radius - 10;
  return above < 18 ? cluster.y + cluster.radius + 14 : above;
}
